// Results Schedule diagram element — an in-canvas, type-grouped results view
// that displays hydraulic calculation output alongside the P&ID.
//
// ADR-forward: The `diagram-elements/` concept is a new peer to `vertices/`
// and `edges/`. Vertices/edges participate in hydraulic calculations;
// diagram elements are pure visualization/annotation objects that persist
// with the system but do not affect the solver.
//
// Phase 1 ships Dynamic mode only. Manual mode (`mode: 'manual'`) is in the
// union today so the round-trip schema is stable for Phase 2.

import { z } from 'zod';
import { PointSchema } from '../base/common';

const SizeSchema = z.object({
  width: z.number().positive(),
  height: z.number().positive(),
});

const FilterOperatorSchema = z.enum(['gt', 'lt', 'eq', 'neq', 'between']);

// A filter always references a column by its stable key (from the column registry)
// and an operator. For `between`, `value2` is required; for everything else, `value` is.
export const FilterSchema = z.object({
  id: z.string(),
  columnKey: z.string(),
  operator: FilterOperatorSchema,
  value: z.number(),
  value2: z.number().optional(),
});

// Per-section sort state. Direction is one of 'asc' or 'desc'; null means no sort.
const SortDirectionSchema = z.enum(['asc', 'desc']);
const SortStateSchema = z.object({
  columnKey: z.string(),
  direction: SortDirectionSchema,
});

export const ResultsScheduleSchema = z.object({
  id: z.string(),
  kind: z.literal('results-schedule'),
  position: PointSchema,
  size: SizeSchema,
  mode: z.enum(['dynamic', 'manual']).default('dynamic'),
  search: z.string().default(''),
  filters: z.array(FilterSchema).default([]),
  // Per-type-section column visibility: key is the vertex type string,
  // value is the list of column keys hidden in that section.
  hiddenColumnsByType: z.record(z.string(), z.array(z.string())).default({}),
  // Per-type-section sort state: key is the vertex/edge type string,
  // value is the current sort column + direction (or null for unsorted).
  sortByType: z.record(z.string(), SortStateSchema.nullable()).default({}),
  // Per-type-section custom column order. The array is a list of column
  // keys in the user's preferred visual order. Columns not listed fall
  // through to the registry's default position. Absent entries for a type
  // use the registry's default order. The 'id' column is always rendered
  // first regardless of this field.
  columnOrderByType: z.record(z.string(), z.array(z.string())).default({}),
  // Populated only when mode === 'manual'. Lists the vertex/edge IDs
  // this schedule should show. In Dynamic mode this field is ignored
  // (the schedule shows all devices of included types).
  //
  // Phase 1 Group L populates this on create-from-selection. Phase 2
  // Group US-06 will add an edit-dialog UI to modify it after creation.
  // Until then, Manual-mode schedules are "create-and-forget" — the
  // device list is fixed until the schedule is deleted and re-created.
  selectedDeviceIds: z.array(z.string()).default([]),
  /**
   * Pivot layout toggle. When true, the schedule renders with metric rows
   * and device columns (transposed from the default). Sort is disabled in
   * pivot mode. Filter / search / manual-mode narrowing still apply to
   * devices, so fewer devices means fewer pivot columns. Hidden metrics
   * (per hiddenColumnsByType) are excluded from the metric-row list.
   */
  pivot: z.boolean().default(false),
});

export type ResultsSchedule = z.infer<typeof ResultsScheduleSchema>;
export type Filter = z.infer<typeof FilterSchema>;
export type FilterOperator = z.infer<typeof FilterOperatorSchema>;
export type SortState = z.infer<typeof SortStateSchema>;
export type SortDirection = z.infer<typeof SortDirectionSchema>;
